import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { db, supabase } from '../lib/supabase'
import { SERIES_DATA } from '../data/series'
import { localizeSeries } from '../data/series.pl'
import { localizeEmotionLabel } from '../data/emotions.pl'
import type { Phase, SessionMode } from '../types/session'
import { PHASE_KEYS } from '../types/session'
import SpiralIndicator from '../components/ui/SpiralIndicator'
import ScreenStamp from '../components/ui/ScreenStamp'
import { useScreenId } from '../lib/useScreenId'
import DescentWrapper from '../components/ui/DescentWrapper'
import PhaseTransition from '../components/ui/PhaseTransition'
import Intention from '../components/session/Intention'
import SatoriMoment from '../components/session/SatoriMoment'
import SeriesIntro from '../components/session/SeriesIntro'
import Arrival from '../components/session/Arrival'
import Ground from '../components/session/Ground'
import Feel from '../components/session/Feel'
import See from '../components/session/See'
import InsightCard from '../components/session/InsightCard'
import Shift from '../components/session/Shift'
import Choose from '../components/session/Choose'
import GentleLanding from '../components/session/GentleLanding'
import Close from '../components/session/Close'
import Retained from '../components/ui/Retained'
import ReviewPrompt, { shouldShowReview } from '../components/ReviewPrompt'
import { trackSessionStarted, trackSeriesSessionStarted } from '../lib/analytics'
import { useBrowserBack } from '../hooks/useBrowserBack'
import { SessionBackContext } from '../lib/sessionBack'
import type { BackRegistry } from '../lib/sessionBack'
import { RESUME_LOOKBACK, pickResumable, resolveSessionEntry } from '../lib/sessionResume'
import type { ResumableFields, SessionEntryState } from '../lib/sessionResume'

/** Navigation state without the marker `useBrowserBack` adds to its spare entry. */
function withoutGuard(state: SessionEntryState | null): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...(state ?? {}) }
  delete rest.backGuard
  return rest
}

function reduceToSingle(n: number): number {
  if (n === 11 || n === 22 || n === 33) return n
  return n > 9 ? reduceToSingle(n % 10 + Math.floor(n / 10)) : n
}

function computeLifePath(month: number, day: number, year: number): number {
  const digits = `${month}${day}${year}`.split('').map(Number)
  return reduceToSingle(digits.reduce((a, b) => a + b, 0))
}

/**
 * The stages of a session, in the order a session CAN go. Which of them one
 * person actually passes through varies: the Satori moment only appears when a
 * confirmed pattern meets high intensity, and the insight card follows See
 * either way. See lib/sessionBack.ts for why the page keeps a running order
 * instead of a phase number.
 */
type Stage =
  | 'arrival' | 'ground' | 'feel' | 'see' | 'satori' | 'card'
  | 'shift' | 'choose' | 'landing' | 'close'

/** The phase number shown in the header, spiral and dots for each stage. */
const PHASE_OF_STAGE: Record<Stage, Phase> = {
  arrival: 0, ground: 1, feel: 2, see: 3, satori: 3, card: 3,
  shift: 4, choose: 5, landing: 5, close: 6,
}

/**
 * The stages that open with the full-screen hand-over between phases. The
 * Satori moment, the card, and the landing come in without one, as before.
 */
const HAS_HANDOVER: ReadonlySet<Stage> = new Set<Stage>(['ground', 'feel', 'see', 'shift', 'choose', 'close'])

/**
 * What a resumed session can go back through, in order. `phase_reached` picks
 * the one it resumes at. Arrival is not in the list on purpose: the session
 * already exists, and its Arrival screen would insert a second one.
 */
const RESUME_STAGES: Stage[] = ['ground', 'feel', 'see', 'shift', 'choose', 'close']

/**
 * Where the person is: the stages visited in the order they took them, which
 * one is up, and which have been built.
 *
 * `order` keeps stages AFTER the active one when the person has gone back, so
 * pressing Continue again returns to them as they were left.
 * `mounted` is the stages that exist on the page. A stage is added the first
 * time it is reached and stays (hidden when not active) until it is discarded,
 * so a resumed session does not build, or bill model calls for, phases the
 * person never returns to.
 *
 * Each mounted stage carries a `build` number that is part of its React key.
 * A stage that is discarded and reached again must start from nothing, and React
 * only starts a component from nothing when its key is new: without the number,
 * "discard Feel, then show Feel" would be the same Feel carrying its old state.
 */
interface Built {
  stage: Stage
  build: number
}

interface Nav {
  order: Stage[]
  at: number
  mounted: Built[]
}

/** Stages that are not saved anywhere: a resumed session's order has none of them. */
const INTERSTITIAL: ReadonlySet<Stage> = new Set<Stage>(['satori', 'card', 'landing'])

const START: Nav = { order: ['arrival'], at: 0, mounted: [{ stage: 'arrival', build: 0 }] }

/** `mounted` with `stage` in it: as it was if already built, otherwise newly built. */
function withStage(mounted: Built[], stage: Stage, build: number): Built[] {
  return mounted.some(m => m.stage === stage) ? mounted : [...mounted, { stage, build }]
}

interface SeriesLaunchState {
  seriesId: string
  seriesTitle: string
  sessionPosition: number
  focusTitle: string
  intention: string
  context: string
}

export default function SessionPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // Pre-session state
  const [userId, setUserId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [userLang, setUserLang] = useState('en')
  const [intentionText, setIntentionText] = useState('')
  const [intentionDone, setIntentionDone] = useState(false)
  const [seriesContext, setSeriesContext] = useState<string | undefined>(undefined)
  // Awakening-only: short summary of recurring threads from past sessions.
  // Populated after Arrival for premium users; passed into Ground/See/Shift
  // (the pattern-recognition phases) as background awareness for the guide.
  const [clusterContext, setClusterContext] = useState<string | undefined>(undefined)
  const [seriesLaunch, setSeriesLaunch] = useState<SeriesLaunchState | null>(null)
  // True until user dismisses the SeriesIntro screen (guided sessions only)
  const [showSeriesIntro, setShowSeriesIntro] = useState(false)

  // Session progression: the running order of stages, and the hand-over (if
  // any) that is playing before the next stage is shown.
  const [nav, setNav] = useState<Nav>(START)
  const [pending, setPending] = useState<{ stage: Stage; nav: Nav } | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Accumulated session data
  const [emotion, setEmotion] = useState('')
  const [intensity, setIntensity] = useState(5)
  // Mind-level lens chosen at Arrival — drives prompt branching in Feel/See/Shift/Close
  const [sessionMode, setSessionMode] = useState<SessionMode>('standard')
  const [storyText, setStoryText] = useState('')
  const [bodyLocation, setBodyLocation] = useState('')
  const [reminderText, setReminderText] = useState('')
  const [beliefText, setBeliefText] = useState('')      // Meaning Imprint answer from Feel
  const [expressionText, setExpressionText] = useState('') // Expression Step answer from Feel
  const [ageText, setAgeText] = useState('')            // Optional age answer on the Feel reflection ('' if none)
  const [patternReflection, setPatternReflection] = useState('')
  const [reframeResponse, setReframeResponse] = useState('')
  const [choiceText, setChoiceText] = useState('')
  // The saved summary of a session resumed at Close; cleared when an earlier answer changes.
  const [savedSummary, setSavedSummary] = useState('')

  const [sessionError, setSessionError] = useState<string | null>(null)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  // Pause overlay — shown when user taps the pause button during an active phase.
  // Does not navigate away; user returns to the exact same phase state on dismiss.
  const [showPauseOverlay, setShowPauseOverlay] = useState(false)
  const [blocked, setBlocked] = useState(false)
  // True once an unfinished session has been resumed. The free-tier gate is for
  // STARTING a session; a session whose summary is written counts as used, so
  // without this the gate would shut the person out of their own session.
  const resumedRef = useRef(false)
  const [showReviewPrompt, setShowReviewPrompt] = useState(false)
  const [completedSessionCount, setCompletedSessionCount] = useState(0)
  const [subscriptionTier, setSubscriptionTier] = useState('free')
  const [sessionUser, setSessionUser] = useState<import('@supabase/supabase-js').User | null>(null)
  /**
   * Pre-selected emotion from a "Explore this further" navigation from Close.
   * Initialized synchronously from location.state so Arrival receives it on first render.
   * A useState initializer (not useEffect) ensures Arrival's own useState for 'selected'
   * can safely read this value before mounting.
   */
  const [preselectedEmotion] = useState<string>(
    (location.state as { preselectedEmotion?: string } | null)?.preselectedEmotion ?? ''
  )
  // When non-null, the free-tier cooldown is active — stores when the next session opens
  const [cooldownUntil, setCooldownUntil] = useState<Date | null>(null)
  // Last 3 completed sessions shown in the session-3 handoff screen
  const [handoffSessions, setHandoffSessions] = useState<Array<{ emotion_label: string; created_at: string }>>([])

  // The stage on screen, and what the header, spiral and dots show for it.
  // While a hand-over plays the old stage stays up underneath it.
  const stage = nav.order[nav.at]
  const phase = PHASE_OF_STAGE[stage]
  const transitioningToPhase: Phase | null = pending ? PHASE_OF_STAGE[pending.stage] : null

  // The newest nav, for handlers that read it after an await (the person can
  // press Back while a save is in flight).
  const navRef = useRef(nav)
  useEffect(() => { navRef.current = nav })

  // Numbers each build of a stage (see Nav), so a rebuilt stage is a new component.
  const buildSeq = useRef(0)
  const nextBuild = () => ++buildSeq.current

  // What each stage last handed on to the next, as text. Pressing Continue on a
  // stage the person went back to compares against it to tell "changed my
  // answer" (rebuild everything after) from "changed nothing" (return to the
  // next stage as it was left).
  const handedOn = useRef<Partial<Record<Stage, string>>>({})

  // The highest phase written to the database. `phase_reached` is a high-water
  // mark (Home and the Journal read it as "how far did this session get"), so it
  // only ever goes up; going back and continuing must not lower it.
  const reachedRef = useRef(0)

  // True while the session row is being created (or corrected) at Arrival or the
  // series intro. See handleArrivalComplete.
  const creating = useRef(false)

  // Close's completion side effects (the pattern count, the archetype and signal
  // extraction, the 72-hour-wait notice) belong to the SESSION finishing, once,
  // however many times its summary is written. Close asks here.
  const completionClaimed = useRef(false)
  const claimCompletion = () => {
    if (completionClaimed.current) return false
    completionClaimed.current = true
    return true
  }

  // Who answers Back. Components inside the session sign in here while they are
  // on screen (lib/sessionBack.ts); the page asks them, deepest first.
  const handlers = useRef(new Set<Parameters<BackRegistry['register']>[0]>())
  const backRegistry = useMemo<BackRegistry>(() => ({
    register(entry) {
      handlers.current.add(entry)
      return () => { handlers.current.delete(entry) }
    },
  }), [])

  // Cross-session context
  const [returnContext, setReturnContext] = useState<{ pattern: string; cardTitle?: string } | null>(null)
  const [personalSymbol, setPersonalSymbol] = useState<string | null>(null)
  const [lifePathNumber, setLifePathNumber] = useState<number | undefined>(undefined)

  const phaseName = phase > 0 ? t(`session.phases.${PHASE_KEYS[phase as Phase]}`) : null

  // The two screens a free account can be stopped at are drawn by this page,
  // so this page reports their numbers. Every other screen reports its own.
  useScreenId(blocked ? (cooldownUntil ? 'gate.cooldown' : 'gate.limit') : null)

  // Load first name + check free tier gate on mount
  useEffect(() => {
    supabase?.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      setUserId(user.id)
      setSessionUser(user)

      // Load display name + personal symbol — show onboarding if not set
      db()
        .from('profiles')
        .select('display_name, personal_symbol, birth_month, birth_day, birth_year, language')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          const profile = data as { display_name: string | null; personal_symbol?: string | null; birth_month?: number | null; birth_day?: number | null; birth_year?: number | null; language?: string | null } | null
          setFirstName(profile?.display_name ?? '')
          setUserLang(profile?.language ?? 'en')
          setPersonalSymbol(profile?.personal_symbol ?? null)
          if (profile?.birth_month && profile?.birth_day && profile?.birth_year) {
            setLifePathNumber(computeLifePath(profile.birth_month, profile.birth_day, profile.birth_year))
          }
        })

      // Load cross-session context: last completed session pattern + saved archetype card title
      db()
        .from('sessions')
        .select('id, pattern_reflection')
        .eq('user_id', user.id)
        .eq('is_complete', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
        .then(({ data: lastSession }) => {
          if (!lastSession) return
          const ls = lastSession as { id: string; pattern_reflection: string | null }
          if (!ls.pattern_reflection) return
          db()
            .from('insight_cards')
            .select('card_title')
            .eq('session_id', ls.id)
            .eq('card_type', 'pattern_archetype')
            .eq('saved', true)
            .maybeSingle()
            .then(({ data: card }) => {
              setReturnContext({
                pattern: ls.pattern_reflection!,
                cardTitle: (card as { card_title: string } | null)?.card_title ?? undefined,
              })
            })
        })

      // Free tier gate: enforce 3-session limit and 72h cooldown between sessions
      Promise.all([
        // Total completed session count
        db()
          .from('sessions')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          // A free session is used once its summary is written (lib/freeTier.ts).
          .not('session_summary', 'is', null),
        // Subscription tier
        db()
          .from('profiles')
          .select('subscription_tier')
          .eq('id', user.id)
          .maybeSingle(),
        // Last 3 completed sessions — used for cooldown check and the session-3 handoff screen
        db()
          .from('sessions')
          .select('id, emotion_label, created_at')
          .eq('user_id', user.id)
          .not('session_summary', 'is', null)
          .order('created_at', { ascending: false })
          .limit(3),
      ]).then(([sessionsRes, profileRes, recentRes]) => {
        const completed = sessionsRes.count ?? 0
        const tier = (profileRes.data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
        setCompletedSessionCount(completed)
        setSubscriptionTier(tier)
        const recent = (recentRes.data as Array<{ id: string; emotion_label: string; created_at: string }>) ?? []

        if (tier !== 'free') return  // paid tiers: no gate

        if (completed >= 3) {
          // Hard gate: all 3 free sessions used — show handoff screen
          if (resumedRef.current) return
          setBlocked(true)
          setHandoffSessions(recent)
          return
        }

        if (completed > 0 && recent.length > 0) {
          // Cooldown gate: enforce 72h between sessions
          const lastSessionTime = new Date(recent[0].created_at)
          const cooldownEnd = new Date(lastSessionTime.getTime() + 72 * 60 * 60 * 1000)
          if (new Date() < cooldownEnd) {
            if (resumedRef.current) return
            setCooldownUntil(cooldownEnd)
            setBlocked(true)
          }
        }
      })
    })
  }, [])

  /**
   * Load an unfinished session and put the page back where the person left it:
   * every saved answer restored, at the step they had reached.
   *
   * A session that has since been completed is left alone. That happens when
   * someone finishes, lands on Home, and presses back: the history entry still
   * names the session, but there is nothing in it to return to, and reopening
   * its last step would write its closing summary a second time.
   */
  function resumeSession(id: string) {
    db()
      .from('sessions')
      .select('id, emotion_label, emotion_intensity, story_text, body_location, reminder_text, pattern_reflection, pattern_confirmed, reframe_response, choice_text, intention_text, phase_reached, is_complete, session_summary, session_mode, belief_text, expression_text, age_text')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return
        const s = data as {
          is_complete: boolean | null
          id: string
          emotion_label: string | null
          emotion_intensity: number | null
          story_text: string | null
          body_location: string | null
          reminder_text: string | null
          pattern_reflection: string | null
          pattern_confirmed: boolean | null
          reframe_response: string | null
          choice_text: string | null
          intention_text: string | null
          phase_reached: number
          // Saved since #69. Rows from before it have the lens defaulted by the
          // database and the other three NULL, which read as "none given".
          session_summary: string | null
          session_mode: SessionMode | null
          belief_text: string | null
          expression_text: string | null
          age_text: string | null
        }
        if (s.is_complete) return
        // Resuming is not starting: the free-tier gate does not apply to this session.
        resumedRef.current = true
        setBlocked(false)
        setSessionId(s.id)
        setEmotion(s.emotion_label ?? '')
        setIntensity(s.emotion_intensity ?? 5)
        setStoryText(s.story_text ?? '')
        setBodyLocation(s.body_location ?? '')
        setReminderText(s.reminder_text ?? '')
        setPatternReflection(s.pattern_reflection ?? '')
        setReframeResponse(s.reframe_response ?? '')
        setChoiceText(s.choice_text ?? '')
        // What was set in Arrival and Feel, so See, Shift and Close run in the same
        // lens and with the same belief, expression and age as before the reload.
        setSessionMode(s.session_mode ?? 'standard')
        // A session resumed at Close with its summary already written shows that
        // summary instead of asking for it again.
        setSavedSummary(s.phase_reached >= 6 ? (s.session_summary ?? '') : '')
        setBeliefText(s.belief_text ?? '')
        setExpressionText(s.expression_text ?? '')
        setAgeText(s.age_text ?? '')
        if (s.intention_text) setIntentionText(s.intention_text)
        setIntentionDone(true)
        // Resume at the stage the person had reached, with the stages before it
        // in the running order so Back has somewhere to go. Only the stage being
        // resumed is built: the earlier ones are built if and when Back reaches
        // them, so resuming asks the model for nothing it does not need.
        const at = Math.min(Math.max(s.phase_reached, 1), RESUME_STAGES.length) - 1
        const order = RESUME_STAGES.slice(0, at + 1)
        setNav({ order, at, mounted: [{ stage: order[at], build: nextBuild() }] })
        reachedRef.current = s.phase_reached
        // What the stages before this one handed on, as the database has it, so a
        // Continue after going back can tell "same answer" from "changed answer".
        // Feel's four stored answers are compared the same way handleFeelComplete
        // hands them on (body, memory, belief, expression, age). The belief,
        // expression and age used to be unstored, which is why Feel was left out.
        const known: Partial<Record<Stage, string>> = {
          ground: JSON.stringify([s.story_text ?? '']),
          feel: JSON.stringify([s.body_location ?? '', s.reminder_text ?? '', s.belief_text ?? '', s.expression_text ?? '', s.age_text ?? '']),
          see: JSON.stringify([s.pattern_reflection ?? '', !!s.pattern_confirmed]),
          shift: JSON.stringify([s.reframe_response ?? '']),
          choose: JSON.stringify([s.choice_text ?? '']),
        }
        handedOn.current = {}
        for (const stage of order.slice(0, at)) {
          if (known[stage] !== undefined) handedOn.current[stage] = known[stage]
        }
      })
  }

  // Decide, once, what opening this page means (#46).
  //   - Home's "session in progress" card, or a reload: resume that session.
  //   - "Begin a session" and the other deliberate starts: start new.
  //   - Nothing said at all (the browser's forward button, a bookmark, a typed
  //     address): resume an unfinished session if there is one. Before this,
  //     that case always opened an empty first screen, which looked like the
  //     session had been lost.
  useEffect(() => {
    const entry = resolveSessionEntry(location.state as SessionEntryState | null)
    if (entry.kind === 'resume') { resumeSession(entry.id); return }
    if (entry.kind === 'fresh') return
    supabase?.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      db()
        .from('sessions')
        .select('id, is_complete, phase_reached')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(RESUME_LOOKBACK)
        .then(({ data }) => {
          const unfinished = pickResumable((data as ResumableFields[] | null) ?? [])
          if (unfinished) resumeSession(unfinished.id)
        })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bootstrap series session from navigation state (set by Journal "Begin"/"Continue" button)
  useEffect(() => {
    const launch = (location.state as { seriesLaunch?: SeriesLaunchState } | null)?.seriesLaunch
    if (!launch) return
    const ctx = `This is session ${launch.sessionPosition} of the guided series "${launch.seriesTitle}": "${launch.focusTitle}". ${launch.context}`
    setSeriesContext(ctx)
    setSeriesLaunch(launch)
    setIntentionText(launch.intention)
    setIntentionDone(true)
    setShowSeriesIntro(true)  // Show the intro screen before any phase begins
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Scroll to top on every stage change so Android/mobile starts at the top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [stage])

  // ── Moving between stages ─────────────────────────────────────────────────

  /**
   * Go on to `to` from the stage that is up.
   *
   * `changed` says whether what the stage just handed on differs from what it
   * handed on the last time through (it is false the first time). Going on
   * after going back has two outcomes:
   *   - nothing changed: the next stage in the running order comes back exactly
   *     as it was left, with no hand-over and no new model calls.
   *   - something changed: every stage after this one was written from the old
   *     answer, so they are all discarded and `to` is built fresh.
   */
  function advance(to: Stage, changed: boolean) {
    const cur = navRef.current
    const after = cur.order[cur.at + 1]
    // The Satori moment, the insight card and the gentle landing are not saved
    // anywhere, so a resumed session's running order has none of them. When the
    // answer is unchanged and the next stage in the order is a real one, the
    // person goes to it: offering an interstitial again would show a second card.
    const skipsInterstitial = !!after && INTERSTITIAL.has(to) && !INTERSTITIAL.has(after)
    if (!changed && (after === to || skipsInterstitial)) {
      to = after
      setNav({ order: cur.order, at: cur.at + 1, mounted: withStage(cur.mounted, to, nextBuild()) })
      return
    }
    const dropped = cur.order.slice(cur.at + 1)
    for (const gone of dropped) delete handedOn.current[gone]
    const next: Nav = {
      order: [...cur.order.slice(0, cur.at + 1), to],
      at: cur.at + 1,
      mounted: withStage(cur.mounted.filter(m => !dropped.includes(m.stage)), to, nextBuild()),
    }
    if (HAS_HANDOVER.has(to)) setPending({ stage: to, nav: next })
    else setNav(next)
  }

  /**
   * Record what a stage is handing on and say whether it differs from last
   * time. With nothing to compare against (the first time through, or a resumed
   * session whose earlier stages were never walked) it counts as changed: a
   * stage kept from before cannot be trusted to have been written from this answer.
   */
  function handsOn(from: Stage, ...values: unknown[]): boolean {
    const now = JSON.stringify(values)
    const before = handedOn.current[from]
    handedOn.current[from] = now
    return before === undefined || before !== now
  }

  /**
   * Save a stage's answer, and move `phase_reached` to `n` when that is right.
   *
   * Going Back never lowers the stored mark: the person has still been that far.
   * Continuing with a CHANGED answer does set it to `n`, in the same update that
   * stores the answer, because every stage after this one is about to be
   * rebuilt. Left high, a reload before they finished would resume them at a
   * stage whose text was written from the old answer.
   *
   * The remembered mark moves only once the write has gone through, so a failed
   * write is tried again by the next Continue instead of being skipped.
   */
  async function saveProgress(fields: Record<string, unknown>, n: number, changed: boolean) {
    if (!sessionId) return
    const write = changed ? n !== reachedRef.current : n > reachedRef.current
    const { error } = await db()
      .from('sessions')
      .update(write ? { ...fields, phase_reached: n } : fields)
      .eq('id', sessionId)
    if (!error && write) reachedRef.current = n
  }

  /**
   * One step back. The single definition of "back" in a session: the arrow in
   * the header calls it, and so does the browser's back button (below).
   *
   * The innermost thing on screen is asked first (an insight card, a grounding
   * exercise, one of Sedona's questions, a phase's own earlier screen). Only
   * when none of them has an earlier screen does the page go to the previous
   * stage, which is still showing the last screen it showed. At the very first
   * screen the person is asked whether to leave.
   *
   * @returns true if the person is still in the session (a screen went back, or
   *          they were asked whether to leave); false if this call took them out
   *          to Home.
   */
  function handleBack(): boolean {
    // The session row is still being written; there is nothing safe to go back to.
    if (creating.current) return true
    const asked = [...handlers.current].sort((a, b) => b.depth - a.depth)
    for (const handler of asked) {
      if (handler.run() === 'handled') return true
    }

    const previous = nav.order[nav.at - 1]
    // A guided series never shows Arrival, so Arrival is not "the previous
    // screen" even though it sits first in the running order.
    if (previous && !(previous === 'arrival' && seriesLaunch)) {
      setNav({ order: nav.order, at: nav.at - 1, mounted: withStage(nav.mounted, previous, nextBuild()) })
      return true
    }
    if (phase > 0 || intentionDone) {
      setShowExitConfirm(true)
      return true
    }
    navigate('/')
    return false
  }

  // ── Browser back and reload (#46) ─────────────────────────────────────────

  // What this page was first opened with, minus the spare-entry marker.
  const openedWith = useRef<Record<string, unknown>>(withoutGuard(location.state as SessionEntryState | null))

  /**
   * What the history entry being shown should say about this visit. A reload
   * restores exactly this, so once a session exists the entry names it and
   * drops whatever the page was first opened with: after a reload this is a
   * session to return to, not a new one to begin.
   */
  const entryState = (): Record<string, unknown> =>
    sessionId ? { resumeSession: { id: sessionId } } : openedWith.current

  // The browser's back button does what the arrow does. While one step is
  // handing over to the next the arrow is disabled, so back does nothing then
  // too, and the person stays where they are.
  useBrowserBack(
    () => (transitioningToPhase !== null ? true : handleBack()),
    entryState,
  )

  // As soon as a session exists (created at Arrival, or resumed), write it into
  // the entry being shown, so a reload comes back to this session.
  useEffect(() => {
    if (!sessionId) return
    const state = location.state as SessionEntryState | null
    if (!state?.backGuard || state.resumeSession?.id === sessionId) return
    navigate(location.pathname + location.search, {
      replace: true,
      state: { resumeSession: { id: sessionId }, backGuard: true },
    })
  }, [sessionId, location.state, location.pathname, location.search, navigate])

  function handleIntentionComplete(text: string) {
    setIntentionText(text)
    setIntentionDone(true)
  }

  /**
   * Guided series: replaces handleArrivalComplete when a seriesLaunch is active.
   * Uses the session focus title as the emotion label (more meaningful in the
   * journal than a generic emotion word), and skips the Arrival phase entirely.
   */
  async function handleSeriesIntroComplete(int: number) {
    if (!seriesLaunch || creating.current) return
    creating.current = true
    try {
      await startSeriesSession(int, seriesLaunch)
    } finally {
      creating.current = false
    }
  }

  async function startSeriesSession(int: number, seriesLaunch: SeriesLaunchState) {
    setIntensity(int)
    // Use focus title as the emotion label so the journal entry is descriptive
    const emotionLabel = seriesLaunch.focusTitle
    setEmotion(emotionLabel)
    setSessionError(null)

    const { data: { user } } = await supabase!.auth.getUser()
    if (!user) { navigate('/login'); return }

    const insertPayload: Record<string, unknown> = {
      user_id: user.id,
      emotion_label: emotionLabel,
      emotion_intensity: int,
      story_text: '',
      phase_reached: 1,
      intention_text: seriesLaunch.intention,
      series_id: seriesLaunch.seriesId,
      series_session_position: seriesLaunch.sessionPosition,
    }

    const { data: session, error } = await db()
      .from('sessions')
      .insert(insertPayload)
      .select('id')
      .single()

    if (error || !session) {
      // The database's own message is English and for developers; the person sees
      // the catalogue line. The raw text goes to the console in development.
      if (import.meta.env.DEV) console.error('Series session create failed:', error)
      setSessionError(t('errors.sessionStart'))
      return
    }

    setSessionId((session as { id: string }).id)
    reachedRef.current = 1
    trackSeriesSessionStarted(seriesLaunch.seriesId, seriesLaunch.sessionPosition)
    setShowSeriesIntro(false)
    advance('ground', false)  // Skip Arrival (phase 0) — go straight to Ground (phase 1)
  }

  async function handleArrivalComplete(sel: string, int: number, mode: SessionMode) {
    // A second tap while the session is still being created does nothing, and
    // Back cannot leave meanwhile (handleBack): otherwise Back, then a lens, would
    // insert a second session for one visit.
    if (creating.current) return
    creating.current = true
    try {
      await continueFromArrival(sel, int, mode)
    } finally {
      creating.current = false
    }
  }

  async function continueFromArrival(sel: string, int: number, mode: SessionMode) {
    const changed = handsOn('arrival', sel, int, mode)
    setEmotion(sel)
    setIntensity(int)
    // Persist the chosen lens for downstream phases — branches prompts in Feel/See/Shift/Close
    setSessionMode(mode)
    setSessionError(null)

    // Back can return here after the session has started. The session already
    // exists: a second insert would leave two sessions for one visit. Keep the
    // one row, and if the emotion or intensity was changed, correct it and
    // rebuild everything written from the old answer.
    if (sessionId) {
      if (changed) {
        await db().from('sessions').update({ emotion_label: sel, emotion_intensity: int, session_mode: mode }).eq('id', sessionId)
      }
      advance('ground', changed)
      return
    }

    const { data: { user } } = await supabase!.auth.getUser()
    if (!user) { navigate('/login'); return }

    // Build insert — intention_text included only if non-empty (column may not exist yet if migration pending)
    const insertPayload: Record<string, unknown> = {
      user_id: user.id,
      emotion_label: sel,
      emotion_intensity: int,
      session_mode: mode,
      story_text: '',
      phase_reached: 1,
    }
    if (intentionText) insertPayload.intention_text = intentionText
    if (seriesLaunch) {
      insertPayload.series_id = seriesLaunch.seriesId
      insertPayload.series_session_position = seriesLaunch.sessionPosition
    }

    const { data: session, error } = await db()
      .from('sessions')
      .insert(insertPayload)
      .select('id')
      .single()

    if (error || !session) {
      if (import.meta.env.DEV) console.error('Session create failed:', error)
      setSessionError(t('errors.sessionStart'))
      return
    }

    // NixxTech standard: ensure app_memberships row exists (public schema)
    await supabase!.schema('public')
      .from('app_memberships')
      .upsert({ user_id: user.id, app_name: 'inner_awakening' }, { onConflict: 'user_id,app_name' })

    setSessionId(session.id)
    reachedRef.current = 1
    trackSessionStarted()
    if (seriesLaunch) trackSeriesSessionStarted(seriesLaunch.seriesId, seriesLaunch.sessionPosition)

    // Fetch top 3 clusters for context injection (Awakening only).
    // Used as background awareness in Ground/See/Shift — never referenced
    // directly to the user. Excludes clusters dismissed within the past 30 days.
    const { data: profileData } = await db()
      .from('profiles')
      .select('subscription_tier')
      .eq('id', user.id)
      .maybeSingle()
    const userTier = (profileData as { subscription_tier: string } | null)?.subscription_tier ?? 'free'

    if (userTier === 'premium') {
      const now = new Date().toISOString()
      const { data: clusters } = await db()
        .from('pattern_clusters')
        .select('cluster_label, occurrence_count')
        .eq('user_id', user.id)
        .or(`dismissed_until.is.null,dismissed_until.lt.${now}`)
        .gte('occurrence_count', 2)  // only established clusters (2+ signals)
        .order('occurrence_count', { ascending: false })
        .limit(3)

      if (clusters && (clusters as { cluster_label: string; occurrence_count: number }[]).length > 0) {
        const labels = (clusters as { cluster_label: string }[]).map(c => c.cluster_label)
        setClusterContext(
          `Recurring threads from past sessions: ${labels.join(', ')}.`
        )
      }
    }

    advance('ground', false)
  }

  // Each stage's Continue lands here. They all do the same three things: note
  // what is being handed on (so a Continue after going back can tell a changed
  // answer from an unchanged one), save it, and go on. `reach` leaves
  // phase_reached out of the update when it would not rise.

  async function handleGroundComplete(story: string) {
    const changed = handsOn('ground', story)
    setStoryText(story)
    await saveProgress({ story_text: story }, 2, changed)
    advance('feel', changed)
  }

  async function handleFeelComplete(body: string, reminder: string, belief: string, expression: string, age: string) {
    // The age is part of what Feel hands on: changing it counts as a changed answer.
    const changed = handsOn('feel', body, reminder, belief, expression, age)
    setAgeText(age)
    setBodyLocation(body)
    setReminderText(reminder)
    setBeliefText(belief)
    setExpressionText(expression)
    await saveProgress({
      body_location: body,
      reminder_text: reminder,
      belief_text: belief,
      expression_text: expression,
      age_text: age,
    }, 3, changed)
    advance('see', changed)
  }

  async function handleSeeComplete(pattern: string, confirmed: boolean) {
    const changed = handsOn('see', pattern, confirmed)
    setPatternReflection(pattern)
    await saveProgress({ pattern_reflection: pattern, pattern_confirmed: confirmed }, 4, changed)
    // A confirmed pattern at high intensity earns the Satori moment; everyone
    // else goes straight to the pattern's insight card.
    advance(confirmed && intensity >= 6 ? 'satori' : 'card', changed)
  }

  function handleSatoriComplete() {
    advance('card', false)
  }

  function handleInsightCardComplete() {
    advance('shift', false)
  }

  async function handleShiftComplete(reframe: string) {
    const changed = handsOn('shift', reframe)
    setReframeResponse(reframe)
    await saveProgress({ reframe_response: reframe }, 5, changed)
    advance('choose', changed)
  }

  async function handleChooseComplete(choice: string) {
    const changed = handsOn('choose', choice)
    setChoiceText(choice)
    // A changed choice makes the saved summary stale: Close must write a new one.
    if (changed) setSavedSummary('')
    await saveProgress({ choice_text: choice }, 6, changed)
    advance('landing', changed)
  }

  function handleGentleLandingComplete() {
    advance('close', false)
  }

  async function handleCloseComplete() {
    if (seriesLaunch && supabase) {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const seriesData = SERIES_DATA.find(s => s.id === seriesLaunch.seriesId)
        const nextPosition = seriesLaunch.sessionPosition + 1
        const isCompleted = seriesData ? nextPosition > seriesData.session_count : false
        await db()
          .from('user_series_progress')
          .upsert({
            user_id: user.id,
            series_id: seriesLaunch.seriesId,
            current_position: nextPosition,
            last_session_at: new Date().toISOString(),
            ...(isCompleted ? { completed_at: new Date().toISOString() } : {}),
          }, { onConflict: 'user_id,series_id' })
      }
    }
    if (sessionUser && shouldShowReview(completedSessionCount + 1)) {
      setShowReviewPrompt(true)
    } else {
      navigate('/')
    }
  }

  // ── What each stage shows ──────────────────────────────────────────────────

  /**
   * The screen for one stage. A stage that cannot be built yet (the session row
   * does not exist, or the intention is not set) is empty.
   */
  function stageContent(which: Stage): ReactNode {
    if (!intentionDone) return null
    switch (which) {
      case 'arrival':
        // Regular sessions only; a guided series skips this via SeriesIntro.
        // preselectedEmotion is populated when the person arrives from
        // "Explore this further" at Close.
        return seriesLaunch ? null : (
          <Arrival preselectedEmotion={preselectedEmotion} onComplete={handleArrivalComplete} />
        )
      case 'ground':
        return sessionId ? (
          <Ground
            sessionId={sessionId}
            emotion={emotion}
            intensity={intensity}
            intentionText={intentionText}
            firstName={firstName}
            seriesContext={seriesContext}
            clusterContext={clusterContext}
            onComplete={handleGroundComplete}
          />
        ) : null
      case 'feel':
        return sessionId ? (
          <Feel
            sessionId={sessionId}
            emotion={emotion}
            intensity={intensity}
            storyText={storyText}
            firstName={firstName}
            lifePathNumber={lifePathNumber}
            seriesContext={seriesContext}
            sessionMode={sessionMode}
            onComplete={handleFeelComplete}
          />
        ) : null
      case 'see':
        return sessionId ? (
          <See
            sessionId={sessionId}
            emotion={emotion}
            intensity={intensity}
            storyText={storyText}
            bodyLocation={bodyLocation}
            reminderText={reminderText}
            beliefText={beliefText}
            expressionText={expressionText}
            ageText={ageText}
            intentionText={intentionText}
            firstName={firstName}
            lifePathNumber={lifePathNumber}
            seriesContext={seriesContext}
            clusterContext={clusterContext}
            sessionMode={sessionMode}
            onComplete={handleSeeComplete}
          />
        ) : null
      case 'satori':
        return <SatoriMoment onComplete={handleSatoriComplete} />
      case 'card':
        return sessionId ? (
          <InsightCard
            sessionId={sessionId}
            userId={userId}
            emotion={emotion}
            intensity={intensity}
            firstName={firstName}
            intentionText={intentionText}
            storyText={storyText}
            bodyLocation={bodyLocation}
            reminderText={reminderText}
            patternReflection={patternReflection}
            seriesContext={seriesContext}
            onComplete={handleInsightCardComplete}
          />
        ) : null
      case 'shift':
        return (
          <Shift
            emotion={emotion}
            intensity={intensity}
            storyText={storyText}
            patternReflection={patternReflection}
            ageText={ageText}
            sessionId={sessionId ?? ''}
            userId={userId}
            firstName={firstName}
            lifePathNumber={lifePathNumber}
            seriesContext={seriesContext}
            clusterContext={clusterContext}
            sessionMode={sessionMode}
            onComplete={handleShiftComplete}
          />
        )
      case 'choose':
        return sessionId ? (
          <Choose
            sessionId={sessionId}
            userId={userId}
            emotion={emotion}
            firstName={firstName}
            patternReflection={patternReflection}
            reframeResponse={reframeResponse}
            storyText={storyText}
            personalSymbol={personalSymbol}
            lifePathNumber={lifePathNumber}
            seriesContext={seriesContext}
            sessionMode={sessionMode}
            onComplete={handleChooseComplete}
          />
        ) : null
      case 'landing':
        return <GentleLanding firstName={firstName} onComplete={handleGentleLandingComplete} />
      case 'close':
        return sessionId ? (
          <Close
            sessionId={sessionId}
            emotion={emotion}
            intensity={intensity}
            storyText={storyText}
            bodyLocation={bodyLocation}
            reminderText={reminderText}
            beliefText={beliefText}
            expressionText={expressionText}
            patternReflection={patternReflection}
            reframeResponse={reframeResponse}
            choiceText={choiceText}
            ageText={ageText}
            firstName={firstName}
            lifePathNumber={lifePathNumber}
            seriesContext={seriesContext}
            currentSeriesId={seriesLaunch?.seriesId}
            sessionMode={sessionMode}
            claimCompletion={claimCompletion}
            initialSummary={savedSummary || undefined}
            onComplete={handleCloseComplete}
          />
        ) : null
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <SessionBackContext.Provider value={backRegistry}>
    <DescentWrapper phase={phase}>

      {/* Phase transition overlay. The stage being left stays up underneath it;
          the next one is shown (and built, the first time) when it finishes. */}
      {pending !== null && transitioningToPhase !== null && (
        <PhaseTransition
          phase={transitioningToPhase}
          onDone={() => {
            setNav(pending.nav)
            setPending(null)
          }}
        />
      )}

      {/* Exit confirmation overlay */}
      {showExitConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: 'rgba(28,17,40,0.55)', backdropFilter: 'blur(4px)' }}
        >
          <div className="w-full max-w-xs rounded-2xl px-6 py-7 text-center" style={{ background: 'white' }}>
            <p className="font-display text-lg mb-2" style={{ color: '#1C1128', fontStyle: 'italic', fontWeight: 400 }}>
              {t('sessionPage.leaveSession')}
            </p>
            <p className="text-sm mb-6" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
              {t('sessionPage.progressSaved')}
            </p>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 rounded-xl font-medium text-sm"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('sessionPage.leave')}
              </button>
              <button
                onClick={() => setShowExitConfirm(false)}
                className="w-full py-3 rounded-xl text-sm"
                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
              >
                {t('sessionPage.stayInSession')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex items-center px-6 pt-5 pb-3 max-w-xl mx-auto w-full">
        <button
          onClick={handleBack}
          aria-label={t('sessionPage.goBack')}
          disabled={transitioningToPhase !== null}
          className="hover:opacity-70 transition-opacity mr-3 flex-shrink-0"
          style={{ color: '#6B5F7A', background: 'none', border: 'none', cursor: transitioningToPhase !== null ? 'default' : 'pointer', padding: 0, opacity: transitioningToPhase !== null ? 0.3 : 1 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="flex-1 flex flex-col items-center justify-center gap-0.5">
          <SpiralIndicator phase={phase} size={80} />
          {/* Brand name — always visible; dimmed to background during active phases */}
          <span
            className="font-display text-xs tracking-wide"
            style={{ color: phaseName ? '#C4B5D4' : '#9B8FAA', fontStyle: 'italic' }}
          >
            {t('home.appName')}
          </span>
          {/* Phase name + dot progress bar — shown only during active session phases */}
          {phaseName && (
            <>
              <span className="text-xs font-semibold uppercase tracking-widest mt-0.5" style={{ color: '#9B72C0' }}>
                {phaseName}
              </span>
              <div className="flex items-center gap-1.5 mt-1">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div
                    key={i}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: phase >= i ? '#9B72C0' : '#E4D5EE',
                      transition: 'background 0.3s ease',
                    }}
                  />
                ))}
              </div>
            </>
          )}
          {/* Guided series indicator — shown on every screen when inside a series session.
              Includes in-arc progress dots (gold, one per session in the arc) so users
              can see how far into the arc they are at a glance. */}
          {seriesLaunch && !showSeriesIntro && (() => {
            const totalCount = SERIES_DATA.find(s => s.id === seriesLaunch.seriesId)?.session_count ?? 0
            return (
              <div className="flex flex-col items-center gap-1 mt-1">
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(212,175,96,0.1)',
                    border: '1px solid rgba(212,175,96,0.2)',
                    color: 'rgba(212,175,96,0.8)',
                    letterSpacing: '0.02em',
                  }}
                >
                  {t('sessionPage.seriesSessionOf', { title: seriesLaunch.seriesTitle, n: seriesLaunch.sessionPosition, total: totalCount || '?' })}
                </span>
                {/* In-arc progress dots — filled for completed + current session */}
                {totalCount > 0 && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalCount }, (_, i) => (
                      <div
                        key={i}
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: i < seriesLaunch.sessionPosition
                            ? 'rgba(212,175,96,0.85)'
                            : 'rgba(212,175,96,0.2)',
                          transition: 'background 0.3s ease',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })()}
        </div>

        {/* Right-side spacer — becomes pause button during active phases.
            A quiet safety valve: lets users take a breath without losing session state. */}
        {phase >= 1 && !transitioningToPhase ? (
          <button
            onClick={() => setShowPauseOverlay(true)}
            aria-label={t('sessionPage.pauseSession')}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            {/* Two vertical bars — universal pause icon */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="3" y="2" width="4" height="12" rx="1.5" fill="#C4B5D4" />
              <rect x="9" y="2" width="4" height="12" rx="1.5" fill="#C4B5D4" />
            </svg>
          </button>
        ) : (
          <div className="w-[28px] flex-shrink-0" />
        )}
      </nav>

      {/* Pause overlay — full-screen breathing prompt.
          Does not change phase state. User returns to exactly where they were. */}
      {showPauseOverlay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: 'rgba(28,17,40,0.65)', backdropFilter: 'blur(6px)' }}
        >
          <div className="w-full max-w-sm rounded-2xl px-6 py-8 text-center" style={{ background: 'white' }}>
            <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
              {t('sessionPage.paused')}
            </p>
            <p className="font-display text-lg mb-2" style={{ color: '#1C1128', fontStyle: 'italic', fontWeight: 400, lineHeight: 1.4 }}>
              {t('sessionPage.takeABreath')}
            </p>
            <p className="text-sm mb-8" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
              {t('sessionPage.waitingRightHere')}
            </p>
            <button
              onClick={() => setShowPauseOverlay(false)}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('sessionPage.readyToContinue')}
            </button>
          </div>
        </div>
      )}

      {/* Guided series intro — full-screen override shown once before any phase */}
      {showSeriesIntro && seriesLaunch && (() => {
        // seriesTitle / focusTitle / intention already arrive localized in the
        // seriesLaunch payload (Journal localizes before navigating). The tag is
        // read from the arc data here, so it has to be localized on this side too.
        const raw = SERIES_DATA.find(s => s.id === seriesLaunch.seriesId)
        const seriesData = raw ? localizeSeries(raw, userLang) : undefined
        return (
          <SeriesIntro
            seriesTitle={seriesLaunch.seriesTitle}
            seriesTag={seriesData?.tag ?? 'Guided Arc'}
            sessionCount={seriesData?.session_count ?? seriesLaunch.sessionPosition}
            sessionPosition={seriesLaunch.sessionPosition}
            focusTitle={seriesLaunch.focusTitle}
            intention={seriesLaunch.intention}
            onBegin={handleSeriesIntroComplete}
            onBack={() => navigate(-1)}
          />
        )
      })()}

      {/* Intention (pre-phase — shown before Arrival on regular sessions only) */}
      {!intentionDone && !showSeriesIntro && (
        <Intention
          firstName={firstName}
          returnContext={returnContext}
          onComplete={handleIntentionComplete}
        />
      )}

      {/* Free tier gate — two variants: cooldown and session limit */}
      {blocked && cooldownUntil && (
        // ── Cooldown gate: session completed < 72h ago ──────────────────────
        <div className="flex-1 flex flex-col items-center justify-center px-6 page-enter">
          <div className="w-full max-w-sm text-center">
            <p className="text-xs font-medium uppercase tracking-widest mb-6" style={{ color: '#9B72C0' }}>
              {t('sessionPage.giveItTime')}
            </p>

            {/* Session progress dots */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[0, 1, 2].map(i => (
                <div key={i} className="rounded-full" style={{
                  width: i < handoffSessions.length ? 10 : 7,
                  height: i < handoffSessions.length ? 10 : 7,
                  background: i < handoffSessions.length ? '#9B72C0' : '#E4D5EE',
                }} />
              ))}
            </div>

            <h2 className="font-display text-2xl mb-3" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
              {(() => {
                const now = new Date()
                const diffMs = cooldownUntil.getTime() - now.getTime()
                const diffHours = diffMs / (1000 * 60 * 60)
                // Under a day reads as a clock time, beyond that as a weekday + date — both in the active language.
                const when = diffHours < 24
                  ? t('sessionPage.todayAt', { time: cooldownUntil.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' }) })
                  : cooldownUntil.toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })
                return t('sessionPage.sessionOpens', { n: handoffSessions.length + 1, when })
              })()}
            </h2>

            <p className="text-sm mb-8" style={{ color: '#6B5F7A', lineHeight: 1.7 }}>
              {t('sessionPage.carryWhatFound')}
            </p>

            <button
              onClick={() => navigate('/journal')}
              className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('sessionPage.viewJournal')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-xl text-sm"
              style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
            >
              {t('sessionPage.backToHome')}
            </button>
          </div>
        </div>
      )}

      {blocked && !cooldownUntil && (
        // ── Session limit gate: all 3 free sessions complete ────────────────
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full page-enter">

          <p className="text-xs font-medium uppercase tracking-widest mb-6 text-center" style={{ color: '#9B72C0' }}>
            {t('sessionPage.startedSomething')}
          </p>

          {/* What they've uncovered — the case for upgrading without saying "upgrade" */}
          {handoffSessions.length > 0 && (
            <div
              className="mb-6 px-5 py-5 rounded-2xl"
              style={{ background: 'linear-gradient(145deg, #F9F5FF 0%, #F1E8F8 100%)', border: '1.5px solid #CEBCDC' }}
            >
              <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                {t('sessionPage.whatExplored')}
              </p>
              <div className="space-y-2.5">
                {handoffSessions.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#9B72C0' }} />
                    <p className="text-sm" style={{ color: '#1C1128' }}>
                      {localizeEmotionLabel(s.emotion_label, i18n.language)}
                      <span className="ml-2" style={{ color: '#9B8FAA', fontSize: '0.7rem' }}>
                        {new Date(s.created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm mb-8 text-center" style={{ color: '#6B5F7A', lineHeight: 1.7 }}>
            {t('sessionPage.practiceUnlocksFull')}
          </p>

          <button
            onClick={() => navigate('/')}
            className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
            style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {t('sessionPage.upgradeMonthly')}
          </button>
          <p className="text-xs text-center mb-3" style={{ color: '#9B8FAA' }}>
            {t('sessionPage.orAnnualSave')}
          </p>
          <button
            onClick={() => navigate('/journal')}
            className="w-full py-3 rounded-xl text-sm"
            style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
          >
            {t('sessionPage.viewJournal')}
          </button>
        </div>
      )}

      {/* Session content — hidden when blocked by tier limit or cooldown */}
      {!blocked && <>

      {/* Session creation error */}
      {sessionError && (
        <div className="px-6 pb-2 max-w-xl mx-auto w-full">
          <div className="px-4 py-3 rounded-xl" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
            <p className="text-xs" style={{ color: '#DC2626' }}>{sessionError}</p>
          </div>
        </div>
      )}

      {/* The stages. Each one visited is built once and kept (see Retained):
          the one on screen is shown, the others wait out of sight with their
          inputs, their fetched text and their current screen intact, so Back
          can return to any of them exactly as it was left. */}
      {nav.mounted.map(m => (
        <Retained key={`${m.stage}.${m.build}`} active={m.stage === stage}>
          {stageContent(m.stage)}
        </Retained>
      ))}

      </>}

    </DescentWrapper>
    </SessionBackContext.Provider>

    {/* The screen's reference number, for bug reports. Drawn once, here. */}
    <ScreenStamp transitioningTo={transitioningToPhase} />

    {showReviewPrompt && sessionUser && (
      <ReviewPrompt
        user={sessionUser}
        tier={subscriptionTier}
        sessionCount={completedSessionCount + 1}
        onDismiss={() => { setShowReviewPrompt(false); navigate('/') }}
      />
    )}
    </>
  )
}
